import { createFileRoute } from "@tanstack/react-router";
import { t } from "@lingui/core/macro";
import { useBankData } from "@/dataProviders/BankDataProvider.tsx";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { PageHeader } from "@/components/PageHeader.tsx";
import { Card, CardTitle } from "@/components/ui/card.tsx";
import { Trans } from "@lingui/react/macro";
import { Separator } from "@/components/ui/separator.tsx";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { type FormEventHandler, useMemo, useRef, useState } from "react";
import { BankSelect, type BankSelectRef, SEARCH_PARAMS_PAYMENT_PROVIDER } from "@/modules/accounts/BankSelect.tsx";
import {
  BANK_ACCOUNT_KEY,
  useAccountsToConnectQuery,
  useAuthenticateMerchantMutation,
  useConnectAccountsMutation,
} from "@/modules/accounts/api/useAccounts.ts";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import { Loader } from "@/components/Loader.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { cn, NOT_AVAILABLE } from "@/lib/utils.ts";
import { Button } from "@/components/ui/button.tsx";
import { toast } from "sonner";
import { ConnectedAccountsTable } from "@/modules/accounts/ConnectedAccountsTable.tsx";
import { useQueryClient } from "@tanstack/react-query";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { logError } from "@/sentry.ts";

export const Route = createFileRoute("/settings/accounts/")({
  component: RouteComponent,
});

const SEARCH_PARAMS_IDENTIFIER = "bankAccountAuthenticateIdentifier";

function RouteComponent() {
  const title = t`Accounts administration`;
  const { activeApp } = useAppConfig();
  const queryClient = useQueryClient();
  const { filterBanksByCountry, sortiedBankCountries } = useBankData();
  const { mutateAsync: mutateAccountsAsync } = useConnectAccountsMutation({ merchantId: activeApp });

  const bankSelectRef = useRef<BankSelectRef>(null);

  const [isChecked, setIsChecked] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

  const toggleOption = (option: string) => {
    const newSelectedValues = selectedAccounts.includes(option)
      ? selectedAccounts.filter((value) => value !== option)
      : [...selectedAccounts, option];
    setSelectedAccounts(newSelectedValues);
  };

  const [selectedBank, setSelectedBank] = useState<string | null>(() => {
    return new URL(window.location.href).searchParams.get(SEARCH_PARAMS_PAYMENT_PROVIDER) || null;
  });

  const { mutateAsync: mutateBanksAsync } = useAuthenticateMerchantMutation({ merchantId: activeApp });

  const getBankProviderAccounts: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();

    if (!selectedBank) return;

    try {
      const callbackUrl = new URL(window.location.href);
      callbackUrl.searchParams.delete(SEARCH_PARAMS_PAYMENT_PROVIDER);
      callbackUrl.searchParams.append(SEARCH_PARAMS_PAYMENT_PROVIDER, selectedBank);

      const { redirectUrl } = await mutateBanksAsync({
        providerCode: selectedBank ?? "",
        callbackUrl: callbackUrl.toString(),
      });
      window.location.href = redirectUrl;
    } catch (e) {
      logError(e);
      toast.error("Something went wrong. We were unable to load the data. Please refresh the page and try again.");
    }
  };

  const reconnectBankAccounts = async (bank: string) => {
    try {
      const callbackUrl = new URL(window.location.href);
      callbackUrl.searchParams.delete(SEARCH_PARAMS_PAYMENT_PROVIDER);
      callbackUrl.searchParams.append(SEARCH_PARAMS_PAYMENT_PROVIDER, bank);

      const { redirectUrl } = await mutateBanksAsync({
        providerCode: bank ?? "",
        callbackUrl: callbackUrl.toString(),
      });
      window.location.href = redirectUrl;
    } catch (e) {
      logError(e);
      toast.error("Something went wrong. We were unable to load the data. Please refresh the page and try again.");
    }
  };

  const [bankAccountAuthenticateIdentifier, setBankAccountAuthenticateIdentifier] = useState(
    () => new URL(window.location.href).searchParams.get(SEARCH_PARAMS_IDENTIFIER) || undefined,
  );

  const { data, isPending, isError } = useAccountsToConnectQuery(
    { merchantId: activeApp },
    { bankAccountAuthenticateIdentifier: bankAccountAuthenticateIdentifier || "" },
    { enabled: !!bankAccountAuthenticateIdentifier },
  );

  const accountsData = useMemo(() => {
    const accountsData: { bankAccountId: string; iban: string }[] = [];
    if (!selectedAccounts.length || !data?.availableAccounts) return accountsData;

    for (const account of selectedAccounts) {
      accountsData.push({
        bankAccountId: account,
        iban: data.availableAccounts.find((item) => item.bankAccountId === account)?.identification?.iban ?? "",
      });
    }
    return accountsData;
  }, [data?.availableAccounts, selectedAccounts]);

  const connectAccounts = async () => {
    try {
      await mutateAccountsAsync({ accountsToConnect: accountsData, bankAccountAuthenticateIdentifier });
      await queryClient.invalidateQueries({ queryKey: [BANK_ACCOUNT_KEY] });
      toast.success(t`Accounts connected successfully.`);
      bankSelectRef.current?.reset();
    } catch (e) {
      logError(e);
      toast.error(t`Something went wrong. We were unable to connect accounts. Please refresh the page and try again.`);
    }
  };

  return (
    <PageWrapper>
      <PageHeader breadcrumbs={[{ href: "/", title }]} />
      <div className="flex flex-col grow p-5">
        <ConnectedAccountsTable className={" s"} onReconnect={reconnectBankAccounts} />
        <Card className={"shadow-none mt-4"}>
          <div className={"px-5"}>
            <CardTitle className={"text-xl font-semibold"}>
              <Trans>Add account</Trans>
            </CardTitle>
          </div>
          <Separator />
          <div className={"px-5"}>
            <div className={"flex gap-4 items-center"}>
              <Checkbox
                className={"cursor-pointer"}
                id={"terms"}
                checked={isChecked}
                onCheckedChange={() => setIsChecked(!isChecked)}
              />
              <p className={"text-sm"}>
                <Trans>
                  I agree to link my account and process my transaction history to match payments made through the
                  Finbricks solution. You can find detailed information on the processing of personal data on the
                  website of Komerční banka, a.s. in the related document.
                </Trans>{" "}
                <a
                  href={"https://www.kb.cz/cs/ochrana-osobnich-udaju"}
                  className={"text-primary underline hover:no-underline"}
                  target={"_blank"}
                  rel={"noreferrer noopener"}
                >
                  <Trans>Information on the processing of personal data.</Trans>
                </a>
              </p>
            </div>

            <Tooltip>
              <TooltipTrigger asChild className={"mt-4"}>
                <div className={"flex gap-4 items-center flex-wrap md:flex-nowrap justify-self-start"}>
                  <BankSelect
                    ref={bankSelectRef}
                    filterBanksByCountry={filterBanksByCountry}
                    sortiedBankCountries={sortiedBankCountries}
                    disabled={!isChecked || !!data}
                    selectedBank={selectedBank}
                    onSelectBank={setSelectedBank}
                    onSubmit={getBankProviderAccounts}
                    onClear={() => setBankAccountAuthenticateIdentifier(undefined)}
                  />
                  <Button
                    variant={"secondary"}
                    type={"button"}
                    onClick={() => bankSelectRef.current?.reset()}
                    disabled={!bankAccountAuthenticateIdentifier}
                  >
                    <Trans>Clear</Trans>
                  </Button>
                </div>
              </TooltipTrigger>
              {!isChecked && (
                <TooltipContent className={"max-w-52"}>
                  <p>
                    <Trans>
                      Please confirm your consent to link your account and allow processing of your transaction history.
                      This step is required before you can select your bank and continue.
                    </Trans>
                  </p>
                </TooltipContent>
              )}
            </Tooltip>

            {isPending && !!bankAccountAuthenticateIdentifier && <Loader message={t`Loading accounts...`} />}
            {isError && (
              <FetchingError
                message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
              />
            )}
            {!!bankAccountAuthenticateIdentifier && data?.availableAccounts && (
              <div>
                <h3 className={"text-sm font-semibold mt-6"}>
                  <Trans>Available accounts</Trans>
                </h3>
                <div className={"flex gap-4 flex-wrap items-end"}>
                  {data.availableAccounts.map((account) => {
                    const isChecked = selectedAccounts.includes(account?.bankAccountId ?? "");
                    return (
                      <label key={account.bankAccountId} htmlFor={account.bankAccountId} className={"mt-2"}>
                        <Card
                          className={cn(
                            "shadow-none flex flex-row items-center px-4 hover:bg-sidebar cursor-pointer py-4",
                            isChecked && "border-primary",
                          )}
                        >
                          <Checkbox
                            id={account.bankAccountId}
                            className={"bg-background cursor-pointer"}
                            onCheckedChange={() => toggleOption(account.bankAccountId ?? "")}
                          />
                          <div>
                            <div className={"flex justify-between items-center"}>
                              <CardTitle className={"text-sm font-semibold"}>{account.accountName}</CardTitle>
                              <span>{account.currency}</span>
                            </div>

                            <p className={"text-sm mt-2"}>{account.identification?.iban}</p>

                            <p className={"text-sm text-muted-foreground mt-1"}>
                              {account.ownersNames?.join(" ,") ?? NOT_AVAILABLE}
                            </p>
                          </div>
                        </Card>
                      </label>
                    );
                  })}
                </div>
                <Button
                  type={"button"}
                  className={"mt-4"}
                  onClick={connectAccounts}
                  disabled={selectedAccounts.length === 0}
                >
                  <Trans>Connect</Trans>
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </PageWrapper>
  );
}
